import { createSlug } from './urlHelpers';

const RECENTLY_VIEWED_STORAGE_KEY = 'neokasa_recently_viewed_v1';
const MAX_RECENTLY_VIEWED = 20;

const resolveShortDescription = (publicData = {}, attributes = {}, raw = {}) => {
  const candidates = [
    raw?.shortDescription,
    raw?.publicData?.short_description,
    raw?.publicData?.shortdescription,
    raw?.publicData?.shortDescription,
    publicData?.short_description,
    publicData?.shortdescription,
    publicData?.shortDescription,
    attributes?.description,
  ];

  const found = candidates.find(value => typeof value === 'string' && value.trim().length > 0);
  return found ? found.trim() : '';
};

const toSubunitAmount = value => {
  if (value == null || value === '') {
    return null;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.round(value * 100);
  }

  const parsed = Number(String(value).replace(',', '.'));
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return Math.round(parsed * 100);
};

const safeRead = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage?.getItem(RECENTLY_VIEWED_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const safeWrite = items => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage?.setItem(RECENTLY_VIEWED_STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    // Ignore storage quota errors.
  }
};

const getPrimaryImageVariants = listing => {
  const firstImage = listing?.images?.[0];
  const variants = firstImage?.attributes?.variants || {};

  const imageUrl =
    variants['scaled-medium']?.url ||
    variants['listing-card']?.url ||
    variants['scaled-small']?.url ||
    variants.default?.url ||
    '';

  return {
    imageUrl,
    variants: {
      default: variants.default?.url || imageUrl || null,
      listingCard: variants['listing-card']?.url || variants.default?.url || imageUrl || null,
      listingCard2x:
        variants['listing-card-2x']?.url ||
        variants['listing-card']?.url ||
        variants.default?.url ||
        imageUrl ||
        null,
      scaledSmall: variants['scaled-small']?.url || imageUrl || null,
      scaledMedium: variants['scaled-medium']?.url || imageUrl || null,
      scaledLarge: variants['scaled-large']?.url || imageUrl || null,
    },
  };
};

const sanitizeRecentItem = raw => {
  const listingId = raw?.listingId ? String(raw.listingId) : null;
  const productTitle = raw?.productTitle ? String(raw.productTitle).trim() : '';
  if (!listingId || !productTitle) {
    return null;
  }

  const slug = raw?.slug ? String(raw.slug) : createSlug(productTitle);
  const priceAmount = Number.isFinite(raw?.priceAmount) ? raw.priceAmount : null;
  const originalPriceAmount = Number.isFinite(raw?.originalPriceAmount) ? raw.originalPriceAmount : null;
  const priceCurrency = raw?.priceCurrency ? String(raw.priceCurrency) : 'EUR';

  return {
    listingId,
    slug,
    url: `/product/${slug}/${listingId}`,
    productTitle,
    shortDescription: resolveShortDescription(raw?.publicData, {}, raw),
    brand: raw?.brand ? String(raw.brand) : '',
    imageUrl: raw?.imageUrl ? String(raw.imageUrl) : '',
    imageVariants: {
      default: raw?.imageVariants?.default || raw?.imageUrl || '',
      listingCard: raw?.imageVariants?.listingCard || raw?.imageUrl || '',
      listingCard2x:
        raw?.imageVariants?.listingCard2x ||
        raw?.imageVariants?.listingCard ||
        raw?.imageVariants?.default ||
        raw?.imageUrl ||
        '',
      scaledSmall: raw?.imageVariants?.scaledSmall || raw?.imageUrl || '',
      scaledMedium: raw?.imageVariants?.scaledMedium || raw?.imageUrl || '',
      scaledLarge: raw?.imageVariants?.scaledLarge || raw?.imageUrl || '',
    },
    priceAmount,
    priceCurrency,
    originalPriceAmount,
    discountPercent: Number.isFinite(raw?.discountPercent) ? raw.discountPercent : null,
    publicData: raw?.publicData && typeof raw.publicData === 'object' ? raw.publicData : {},
    viewedAt: raw?.viewedAt || new Date().toISOString(),
  };
};

export const mapListingToRecentItem = listing => {
  const listingId = listing?.id?.uuid;
  const attributes = listing?.attributes || {};
  const publicData = attributes.publicData || {};
  const title = String(attributes.title || '').trim();
  if (!listingId || !title) {
    return null;
  }

  const slug = createSlug(title);
  const { imageUrl, variants } = getPrimaryImageVariants(listing);

  const listingPrice = attributes.price || null;
  const priceAmount = Number.isFinite(listingPrice?.amount) ? listingPrice.amount : null;
  const priceCurrency = listingPrice?.currency || 'EUR';
  const originalPriceAmount = toSubunitAmount(publicData.original_price);
  const publicShortDescription = resolveShortDescription(publicData, attributes);
  const discountPercent =
    originalPriceAmount && priceAmount && originalPriceAmount > priceAmount
      ? Math.round(((originalPriceAmount - priceAmount) / originalPriceAmount) * 100)
      : null;

  return sanitizeRecentItem({
    listingId,
    slug,
    productTitle: title,
    shortDescription: publicShortDescription,
    brand: publicData.brand || '',
    imageUrl,
    imageVariants: variants,
    priceAmount,
    priceCurrency,
    originalPriceAmount,
    discountPercent,
    publicData: {
      short_description: publicShortDescription,
      shortdescription: publicShortDescription,
      listingType: publicData.listingType,
      transactionProcessAlias: publicData.transactionProcessAlias,
      unitType: publicData.unitType,
      brand: publicData.brand,
      connectivity: publicData.connectivity,
      cardStyle: publicData.cardStyle,
      sales: publicData.sales,
      original_price: publicData.original_price,
    },
    viewedAt: new Date().toISOString(),
  });
};

export const getRecentlyViewedListings = () => {
  const items = safeRead()
    .map(sanitizeRecentItem)
    .filter(Boolean)
    .sort((a, b) => new Date(b.viewedAt).getTime() - new Date(a.viewedAt).getTime());
  return items.slice(0, MAX_RECENTLY_VIEWED);
};

export const pushRecentlyViewedListing = listing => {
  const entry = mapListingToRecentItem(listing);
  if (!entry) {
    return;
  }

  const existing = getRecentlyViewedListings().filter(item => item.listingId !== entry.listingId);
  const next = [entry, ...existing].slice(0, MAX_RECENTLY_VIEWED);
  safeWrite(next);
};

export const toListingCardListing = (item, MoneyType) => {
  const normalized = sanitizeRecentItem(item);
  if (!normalized) {
    return null;
  }

  const price =
    normalized.priceAmount != null
      ? MoneyType
        ? new MoneyType(normalized.priceAmount, normalized.priceCurrency || 'EUR')
        : { amount: normalized.priceAmount, currency: normalized.priceCurrency || 'EUR' }
      : null;

  const imageUrl = normalized.imageUrl || normalized.imageVariants?.default;
  const images = imageUrl
    ? [
        {
          id: { uuid: `${normalized.listingId}_image` },
          type: 'image',
          attributes: {
            variants: {
              default: { url: normalized.imageVariants?.default || imageUrl },
              'listing-card': { url: normalized.imageVariants?.listingCard || imageUrl },
              'listing-card-2x': {
                url:
                  normalized.imageVariants?.listingCard2x ||
                  normalized.imageVariants?.listingCard ||
                  imageUrl,
              },
              'scaled-small': { url: normalized.imageVariants?.scaledSmall || imageUrl },
              'scaled-medium': { url: normalized.imageVariants?.scaledMedium || imageUrl },
              'scaled-large': { url: normalized.imageVariants?.scaledLarge || imageUrl },
            },
          },
        },
      ]
    : [];

  return {
    id: { uuid: normalized.listingId },
    type: 'listing',
    attributes: {
      title: normalized.productTitle,
      price,
      publicData: normalized.publicData || {},
    },
    images,
  };
};

